import { camelizeKeys } from 'humps';
import { computed, defineAsyncComponent, markRaw, unref } from 'vue';

import enumTypes, { ADD_TO_GALLERY_DISABLED_TOOLTIPS } from '@/app/library/constants';
import { useChannelPermissions } from '@/composables/useChannelPermissions';
import { constants } from '@/config';
import { BRAND } from '@/models/auth/permissions.enum';
import { useAddToActionMenuStore } from '@/stores/add-to-action-menu';
import { useAuthStore } from '@/stores/auth';
import { getPlatformFromMediaSource } from '@/stores/campaigns';
import { checkTweetMediaIsOriginal } from '@/utils/media';

export function useAddToGalleryAction(selectedMedia, addMediaToGalleryCallback = () => null) {
  const authStore = useAuthStore();
  const addToActionMenuStore = useAddToActionMenuStore();

  const {
    canAccessTikTok,
    canAccessFacebook,
    canAccessInstagram,
    canAccessX,
    canAccessPinterest,
    canAccessYouTube,
    canAccessLinkedIn,
    canAccessThreads,
  } = useChannelPermissions();

  const formattedSelectedMedia = computed(() =>
    unref(selectedMedia).map((media) => camelizeKeys(media)),
  );

  const containsTikTokUGC = computed(() =>
    formattedSelectedMedia.value.some((media) => media.sourceType === enumTypes.TIKTOK_UGC),
  );

  const containsTextPost = computed(() =>
    formattedSelectedMedia.value.some((media) => media.source?.includes('_TEXT')),
  );

  const containsNonOriginalTweet = computed(() =>
    formattedSelectedMedia.value.some((media) => !checkTweetMediaIsOriginal(media)),
  );

  function everySelectedMediaMatchesSource(source) {
    return formattedSelectedMedia.value.every((media) => {
      const platform = getPlatformFromMediaSource(media.source);
      return platform === source;
    });
  }

  async function addSelectedMediaToSelections() {
    await addToActionMenuStore.addSelectedMediaToSelections(
      formattedSelectedMedia.value,
      addMediaToGalleryCallback,
    );
  }

  function getDisabledGalleryToolTip(galleryType) {
    if (galleryType === constants.TIKTOK) {
      return containsTikTokUGC.value
        ? ADD_TO_GALLERY_DISABLED_TOOLTIPS.CONTAINS_TIKTOK_UGC
        : ADD_TO_GALLERY_DISABLED_TOOLTIPS.TIKTOK;
    }
    if (galleryType === constants.FACEBOOK) {
      return ADD_TO_GALLERY_DISABLED_TOOLTIPS.FACEBOOK;
    }
    if (galleryType === constants.INSTAGRAM_STORY) {
      return ADD_TO_GALLERY_DISABLED_TOOLTIPS.INSTAGRAM_STORY;
    }
    if (galleryType === constants.INSTAGRAM) {
      return ADD_TO_GALLERY_DISABLED_TOOLTIPS.INSTAGRAM;
    }
    if (galleryType === constants.PINTEREST) {
      return ADD_TO_GALLERY_DISABLED_TOOLTIPS.PINTEREST;
    }
    if (galleryType === constants.TIKTOK) {
      return ADD_TO_GALLERY_DISABLED_TOOLTIPS.TIKTOK;
    }
    if (galleryType === constants.YOUTUBE) {
      return ADD_TO_GALLERY_DISABLED_TOOLTIPS.YOUTUBE;
    }
    if (galleryType === constants.LIBRARY) {
      return ADD_TO_GALLERY_DISABLED_TOOLTIPS.CONTAINS_TEXT_POSTS;
    }
    if (galleryType === constants.TWITTER) {
      return containsNonOriginalTweet.value
        ? ADD_TO_GALLERY_DISABLED_TOOLTIPS.CONTAINS_NON_ORIGINAL_POST
        : ADD_TO_GALLERY_DISABLED_TOOLTIPS.X;
    }
    if (galleryType === constants.LINKEDIN) {
      return ADD_TO_GALLERY_DISABLED_TOOLTIPS.LINKEDIN;
    }
    if (galleryType === constants.THREADS) {
      return ADD_TO_GALLERY_DISABLED_TOOLTIPS.THREADS;
    }
    return null;
  }

  const galleryChannelOptions = computed(() => {
    const defaultOptions = [
      {
        value: constants.LIBRARY,
        label: 'Galleries',
        icon: 'gallery',
        hide: !authStore.guard(BRAND.LIBRARY.CAN_ACCESS_LIBRARY),
        disabled: containsTextPost.value,
      },
      {
        value: constants.TIKTOK,
        label: 'TikTok Boards',
        icon: 'tiktok-color',
        hide: !canAccessTikTok.value,
        disabled: containsTikTokUGC.value || !everySelectedMediaMatchesSource(constants.TIKTOK),
      },
      {
        value: constants.INSTAGRAM,
        label: 'Instagram Boards',
        icon: 'instagram-color',
        hide: !canAccessInstagram.value,
        disabled: !everySelectedMediaMatchesSource(constants.INSTAGRAM),
      },
      {
        value: constants.INSTAGRAM_STORY,
        label: 'Instagram Story Boards',
        icon: 'instagram-story-color',
        hide: !canAccessInstagram.value,
        disabled: !everySelectedMediaMatchesSource(constants.INSTAGRAM_STORY),
      },
      {
        value: constants.FACEBOOK,
        label: 'Facebook Boards',
        icon: 'facebook-color',
        hide: !canAccessFacebook.value,
        disabled: !everySelectedMediaMatchesSource(constants.FACEBOOK),
      },
      {
        value: constants.PINTEREST,
        label: 'Pinterest Boards',
        icon: 'pinterest-color',
        hide: !canAccessPinterest.value,
        disabled: !everySelectedMediaMatchesSource(constants.PINTEREST),
      },
      {
        value: constants.TWITTER,
        label: 'X Boards',
        icon: 'x-color',
        hide: !canAccessX.value,
        disabled:
          containsNonOriginalTweet.value || !everySelectedMediaMatchesSource(constants.TWITTER),
      },
      {
        value: constants.YOUTUBE,
        label: 'YouTube Boards',
        icon: 'youtube-color',
        hide: !canAccessYouTube.value,
        disabled: !everySelectedMediaMatchesSource(constants.YOUTUBE),
      },
      {
        value: constants.LINKEDIN,
        label: 'LinkedIn Boards',
        icon: 'linkedin-color',
        hide: !canAccessLinkedIn.value,
        disabled: !everySelectedMediaMatchesSource(constants.LINKEDIN),
      },
      {
        value: constants.THREADS,
        label: 'Threads Boards',
        icon: 'threads-color',
        hide: !canAccessThreads.value,
        disabled: !everySelectedMediaMatchesSource(constants.THREADS),
      },
    ];

    return defaultOptions.map((option) => ({
      command: addSelectedMediaToSelections,
      form: markRaw(defineAsyncComponent(() => import('@/components/AddToGallerySelectList.vue'))),
      formProps: {
        galleryType: option.value,
      },
      badge: addToActionMenuStore.selectedGalleriesMap[option.value]?.length,
      tooltip: option.disabled ? getDisabledGalleryToolTip(option.value) : undefined,
      ...option,
    }));
  });

  return {
    galleryChannelOptions,
    addSelectedMediaToSelections,
  };
}
